// Cycle-16 rich-text editor primitive (`PingRichTextEditor` mirror).
// Renders one of four states from the Figma `Rich Text Editor` frame
// (`2829:4379`). The host owns text state + format toggles; this
// primitive is a controlled view that emits state-change callbacks. No
// IO, no SDK access, no logging.
//
// Visual contract (from `composer-extracted.md` §1–§3):
//   - input pill: 40 tall, paddings L10 R10, itemSpacing 10, pill radius
//   - dark fill `#222429` / light `surface.secondary` (`#F5F5F7`)
//   - text style: Lato 400 16/22.4 — `text.primary` for content,
//                 `#6B7280` / `text.tertiary` for placeholder
//   - format toolbar (Figma `2829:4274`) sits ABOVE the pill when active
//   - mention popover (Figma `2918:10419`) sits ABOVE the editor when
//     the user types `@` and candidates are available

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Platform, Pressable, StyleSheet, Text, type TextStyle, TextInput, type NativeSyntheticEvent, type TextInputSelectionChangeEventData, type TextInputKeyPressEventData, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import type { RichTextEditorState } from './RichTextEditorState';
import { MentionPicker, useMentionPicker } from './MentionPicker';
import type { MentionCandidate } from '../../chat/ChatRoom.types';
import type { RichTextFormat } from './RichTextFormat';
import {
  handleBackspaceAtBlockStart,
  handleTabInList,
  isInsideCodeBlock,
  resolveShortcut,
} from './richTextActions';

export {
  RICH_TEXT_EDITOR_STATES,
  type RichTextEditorState,
} from './RichTextEditorState';

export interface RichTextEditorProps {
  state: RichTextEditorState;
  text: string;
  placeholder: string;
  attachmentLabel?: string | undefined;
  onChangeText: (next: string) => void;
  onChangeState: (next: RichTextEditorState) => void;
  onRemoveAttachment?: (() => void) | undefined;
  /** Optional focus event hook. Host uses this to drive the mic ↔ send
   *  swap in the composer chrome. */
  onFocus?: (() => void) | undefined;
  /** Optional blur event hook — paired with `onFocus`. */
  onBlur?: (() => void) | undefined;
  /**
   * Submit handler invoked when the user presses Enter (and the
   * `pressEnterToSend` policy is satisfied). The host typically
   * wires this to the same send pipeline as the send button.
   */
  onSubmit?: (() => void) | undefined;
  /**
   * When true (default), pressing Enter submits the message and
   * Shift+Enter inserts a newline. When false, Enter inserts a
   * newline and the only way to send is the send button.
   * Mirrors the user-facing "Press Enter to send" setting.
   *
   * NOTE: when the caret sits inside a fenced code block
   * (`` ``` … ``` ``), the Enter behavior is INVERTED — plain
   * Enter always inserts a newline and Shift+Enter is what sends.
   * Implemented in the key handler below regardless of
   * `pressEnterToSend`.
   */
  pressEnterToSend?: boolean | undefined;
  /** Optional mention directory. When set, `@` triggers the picker. */
  mentionCandidates?: ReadonlyArray<MentionCandidate> | undefined;
  /**
   * Selection callback. Fires on every selection change with the
   * current `(start, end)` range so the host can recompute the
   * active-format set and update the toolbar chip highlights.
   * Distinct from the legacy host-tracked caret used by the mention
   * parser — that one only needs the end position; format-active
   * detection needs both ends.
   */
  onSelectionRangeChange?: ((start: number, end: number) => void) | undefined;
  /**
   * Cmd/Ctrl-shortcut callback. Fires when the user presses one of
   * the documented keyboard shortcuts (Cmd+B, Cmd+I, Cmd+Shift+X,
   * etc. — see `richTextActions.ts` for the full table). The host
   * owns the text buffer + selection, so it applies the toggle and
   * propagates the new state back via `text` / external selection
   * update. The editor consumes the event (`preventDefault`) so the
   * textarea doesn't insert the key as text.
   */
  onShortcut?:
    | ((action:
        | { readonly kind: 'format'; readonly format: RichTextFormat }
        | { readonly kind: 'list'; readonly listKind: 'numbered' | 'bulleted' }
        | { readonly kind: 'link' }) => void)
    | undefined;
  /**
   * Optional imperative selection override. The host sets this
   * immediately after a toolbar / shortcut action that rewrites the
   * buffer so the textarea places the caret at the post-action
   * position instead of where the OS would have left it. The prop
   * is one-shot — the host typically clears it on the next
   * `onSelectionRangeChange` callback.
   */
  selection?: { start: number; end: number } | undefined;
  testID?: string | undefined;
}

/**
 * Imperative handle exposed via `forwardRef`. The host calls
 * `focus()` after a toolbar / shortcut action so the textarea
 * regains focus and the post-action selection becomes visible
 * (RN's `selection` prop is no-op while the input is blurred).
 */
export interface RichTextEditorHandle {
  focus(): void;
}

const LONG_TEXT_THRESHOLD = 280;

const RichTextEditorInner = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      state,
      text,
      placeholder,
      attachmentLabel,
      onChangeText,
      onChangeState,
      onRemoveAttachment,
      onFocus,
      onBlur,
      onSubmit,
      pressEnterToSend = true,
      mentionCandidates,
      onSelectionRangeChange,
      onShortcut,
      selection,
      testID,
    },
    ref,
  ) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // The composer's input is a `<textarea>` so it supports
    // multi-line content (Shift+Enter / Enter newline behavior per
    // `pressEnterToSend`). Vertical centering is achieved by
    // explicit padding (see `styles.input` below) — RN-Web's
    // textarea ignores `textAlignVertical: 'center'`. `longText` and
    // `expanded` states are kept for API parity; the height grows
    // from the centered baseline as content wraps.
    const isMultiline = true;
    const showAttachmentChip = state === 'attachmentAttached';

    const inputRef = useRef<TextInput | null>(null);
    // Imperative handle for host-driven focus restoration. The
    // composer's toolbar buttons live OUTSIDE the textarea, so
    // clicking them blurs the input on web — we expose `focus()`
    // so the host can refocus after a toolbar / shortcut action.
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          inputRef.current?.focus();
        },
      }),
      [],
    );
    const [caret, setCaret] = useState(0);
    // Selection range — distinct from `caret` (which is the
    // mention-parser anchor at selection END). The format-active
    // check needs both ends to decide whether a marker wraps the
    // selection or the caret sits between adjacent markers.
    const [selStart, setSelStart] = useState(0);
    const [selEnd, setSelEnd] = useState(0);

    // Auto-grow the `<textarea>` on react-native-web. Browsers don't
    // auto-resize textareas natively, so we reset `height: auto`
    // before reading `scrollHeight` to get the content's natural
    // height, then snap the element height to that value. Clamped
    // between the empty-state baseline (40, matches the pill chrome
    // — see Figma `2804:26707`) and a generous max (240, matches
    // the `longText` state) so multiline messages still scroll
    // inside the pill once they exceed the visible window.
    const MIN_INPUT_HEIGHT = 40;
    const MAX_INPUT_HEIGHT = 240;
    const resizeInput = useCallback(() => {
      const node = inputRef.current as unknown as
        | (HTMLTextAreaElement & { style: CSSStyleDeclaration })
        | null;
      if (
        node === null ||
        typeof node !== 'object' ||
        !('style' in node) ||
        !('scrollHeight' in node)
      ) {
        return;
      }
      // Reset to natural height so `scrollHeight` reflects content,
      // not the previously-pinned height.
      node.style.height = 'auto';
      const content = node.scrollHeight;
      const next = Math.min(
        MAX_INPUT_HEIGHT,
        Math.max(MIN_INPUT_HEIGHT, content),
      );
      node.style.height = `${next}px`;
      // Toggle the inner scroll once we hit the cap so the user can
      // keep typing without losing visible feedback.
      node.style.overflowY = content > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
    }, []);

    // Re-run resize on every text change AND on mount (so the empty
    // state lands at the 40-px baseline instead of the browser
    // default).
    useEffect(() => {
      resizeInput();
    }, [text, resizeInput]);

    const mentions = useMentionPicker(text, caret, mentionCandidates ?? []);

    const handleFocus = useCallback(() => {
      if (state === 'collapsed') {
        onChangeState('expanded');
      }
      onFocus?.();
    }, [state, onChangeState, onFocus]);

    const handleBlur = useCallback(() => {
      onBlur?.();
    }, [onBlur]);

    // `longText` is the chip-style overflow indicator for very long
    // bodies; the host can also drive it directly. We promote
    // `expanded` -> `longText` when the user crosses the threshold to
    // keep the visual spec accurate without taking text ownership.
    const handleChangeText = useCallback(
      (next: string) => {
        onChangeText(next);
        if (state === 'expanded' && next.length >= LONG_TEXT_THRESHOLD) {
          onChangeState('longText');
        } else if (state === 'longText' && next.length < LONG_TEXT_THRESHOLD) {
          onChangeState('expanded');
        }
      },
      [state, onChangeText, onChangeState],
    );

    const handleSelectionChange = useCallback(
      (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
        const sel = e.nativeEvent.selection;
        setCaret(sel.end);
        setSelStart(sel.start);
        setSelEnd(sel.end);
        onSelectionRangeChange?.(sel.start, sel.end);
      },
      [onSelectionRangeChange],
    );

    const handleSelectMention = useCallback(
      (candidate: MentionCandidate) => {
        const next = mentions.insertMention(candidate, text, caret);
        onChangeText(next.text);
        setCaret(next.caret);
      },
      [mentions, text, caret, onChangeText],
    );

    const handleKeyPress = useCallback(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        const key = e.nativeEvent.key;
        // RN-Web mirrors the underlying KeyboardEvent onto
        // `nativeEvent`; on iOS/Android these modifier flags are
        // absent (optional chains keep that safe). One typed alias
        // covers every block below so we don't repeat the cast.
        const native = e.nativeEvent as TextInputKeyPressEventData & {
          readonly shiftKey?: boolean;
          readonly metaKey?: boolean;
          readonly ctrlKey?: boolean;
          readonly altKey?: boolean;
          preventDefault?: () => void;
        };
        const shift = native.shiftKey === true;
        const meta = native.metaKey === true;
        const ctrl = native.ctrlKey === true;
        const alt = native.altKey === true;
        // Cmd on mac / Ctrl elsewhere is the primary modifier ("Mod").
        // We accept either so the same shortcut fires whether the
        // user is on macOS or a non-mac platform with a USB keyboard.
        const mod = meta || ctrl;

        // Mention popover takes precedence — when it's open, arrows
        // and Enter drive the picker.
        if (mentions.open) {
          if (key === 'ArrowUp' || key === 'ArrowDown') {
            mentions.handleKeyEvent(key);
            return;
          }
          if (key === 'Enter') {
            const selected = mentions.filteredCandidates[mentions.selectedIndex];
            if (selected !== undefined) {
              handleSelectMention(selected);
            }
            return;
          }
          if (key === 'Escape') {
            mentions.handleKeyEvent('Escape');
            return;
          }
        }

        // -------------------------------------------------------
        // Keyboard shortcuts. Cmd/Ctrl + B/I/Shift+X/Shift+C/etc. →
        // format toggle. We resolve here, call into the host's
        // `onShortcut` (which owns the buffer + selection), and
        // consume the key so the textarea doesn't insert a literal
        // character.
        // -------------------------------------------------------
        if (mod && key.length === 1 && onShortcut !== undefined) {
          const action = resolveShortcut({ key, mod: true, shift, alt });
          if (action !== null) {
            native.preventDefault?.();
            onShortcut(action);
            return;
          }
        }

        // -------------------------------------------------------
        // Backspace at the start of a block-prefixed line demotes
        // the block (`> `, `- `, `1. `, etc.) back to a plain
        // paragraph.
        // -------------------------------------------------------
        if (key === 'Backspace' && selStart === selEnd) {
          const demoted = handleBackspaceAtBlockStart(text, selStart, selEnd);
          if (demoted !== null) {
            native.preventDefault?.();
            onChangeText(demoted.text);
            // Caret is already at `lineStart` post-demote — the
            // resulting text emits through `onChangeText` and the
            // host's controlled-input contract will re-render.
            // RN's selection prop drives the textarea selection on
            // the next layout pass.
            setSelStart(demoted.selStart);
            setSelEnd(demoted.selEnd);
            setCaret(demoted.selEnd);
            return;
          }
        }

        // -------------------------------------------------------
        // Tab / Shift+Tab inside a list/quote line: indent or
        // outdent by two spaces. On plain prose we fall through so
        // the textarea handles Tab normally (focus shift on web).
        // -------------------------------------------------------
        if (key === 'Tab' && !mod) {
          const result = handleTabInList(
            text,
            selStart,
            selEnd,
            shift ? 'outdent' : 'indent',
          );
          if (result !== null) {
            native.preventDefault?.();
            onChangeText(result.text);
            setSelStart(result.selStart);
            setSelEnd(result.selEnd);
            setCaret(result.selEnd);
            return;
          }
        }

        // -------------------------------------------------------
        // Enter handling — combines the legacy `pressEnterToSend`
        // policy with code-block Enter inversion.
        //
        //   Outside a code block:
        //     pressEnterToSend === true  → Enter sends, Shift+Enter newline
        //     pressEnterToSend === false → Enter newline (no submit path)
        //
        //   Inside a `` ``` … ``` ``-fence:
        //     Enter always inserts a newline (overrides the policy)
        //     Shift+Enter is the ONLY send key, regardless of policy
        //
        // Enter-in-a-codeblock does NOT send — keeps multi-line
        // snippet editing intact.
        // -------------------------------------------------------
        if (key === 'Enter' && onSubmit !== undefined) {
          const inCodeBlock = isInsideCodeBlock(text, selStart);
          if (inCodeBlock) {
            if (shift) {
              native.preventDefault?.();
              onSubmit();
            }
            // Plain Enter inside a fence falls through → textarea
            // inserts the newline natively.
            return;
          }
          if (pressEnterToSend && !shift) {
            native.preventDefault?.();
            onSubmit();
          }
          // Shift+Enter (or any Enter when pressEnterToSend is false)
          // falls through → the textarea inserts the newline natively.
        }
      },
      [
        mentions,
        handleSelectMention,
        onSubmit,
        pressEnterToSend,
        onShortcut,
        text,
        selStart,
        selEnd,
        onChangeText,
      ],
    );

    // Body text: Lato 400 16/22.4 — explicit `lineHeight: 22` keeps the
    // 40-px pill height exactly centered with 9 px vertical padding.
    // Color stays in `style` because it's theme-driven from the token
    // set; the rest of the body typography moves to `className`.
    const textColorStyle: TextStyle = { color: colors.text.primary };

    return (
      <View
        // Wrapper column — fills the host pill horizontally + vertically
        // and centers the input row in the available space.
        className="flex flex-col flex-1 min-w-0 justify-center"
        testID={testID}
      >
        {/* Picker pinned above the input pill when active. */}
        <MentionPicker
          open={mentions.open}
          candidates={mentions.filteredCandidates}
          query={mentions.query}
          selectedIndex={mentions.selectedIndex}
          onSelect={handleSelectMention}
          onClose={mentions.dismiss}
        />

        <View
          // Transparent inner row — the outer pill chrome (bg, radius,
          // padding) is owned by the composer host. `min-h-10` for the
          // empty/collapsed state; `max-h-[240px]` caps the longText
          // overflow before the textarea starts scrolling.
          className={`
            flex flex-row items-center gap-[10px]
            ${state === 'collapsed' ? 'min-h-10' : ''}
            ${state === 'longText' ? 'max-h-[240px]' : ''}
          `}
        >
          {showAttachmentChip && attachmentLabel ? (
            <View
              className="
                flex flex-row items-center gap-sm self-start
                px-sm py-xxs rounded-sm border
                bg-light-surface-tertiary dark:bg-dark-surface-tertiary
                mb-xs
              "
              style={{
                borderColor: colors.border.primary,
                borderWidth: StyleSheet.hairlineWidth,
              }}
            >
              <Text
                className="font-sans text-footnote font-medium"
                style={{ color: colors.text.secondary }}
              >
                {attachmentLabel}
              </Text>
              {onRemoveAttachment ? (
                <Pressable
                  onPress={onRemoveAttachment}
                  accessibilityRole="button"
                  accessibilityLabel="Remove attachment"
                >
                  <Text
                    className="font-sans text-body font-semibold px-xxs"
                    style={{ color: colors.text.secondary }}
                  >
                    ×
                  </Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}

          <TextInput
            ref={inputRef}
            accessibilityLabel="Message"
            // `numberOfLines={1}` → `rows="1"` on RN-Web's `<textarea>`.
            // Without it the default `rows="2"` would inflate
            // `scrollHeight` to 2×lineHeight even for single-line
            // content. With `rows="1"` the textarea collapses to one
            // line by default and grows from there as the user adds
            // newlines (handled imperatively by `resizeInput`).
            numberOfLines={1}
            // Body text styling — most of it travels via className.
            // `min-h-10` anchors the empty state to the 40-px pill
            // chrome (Figma `2804:26707`); the `resizeInput` effect
            // overrides this imperatively as content grows.
            // `paddingTop/Bottom: 9` centers a single line on the
            // pill's midline (`(40 - 22) / 2 = 9`). RN-Web's
            // `<textarea>` ignores `textAlignVertical: 'center'`, so
            // explicit padding is the only reliable centering hook.
            className="flex-1 min-h-10 p-0 m-0 font-sans text-base leading-[22px]"
            style={[
              textColorStyle,
              // Inline-only normalizations that can't be expressed via
              // NativeWind:
              //   - `paddingTop` / `paddingBottom: 9` (specific to
              //     the 40-px pill math; the imperative resize
              //     anchors growth from this baseline)
              //   - `borderWidth: 0` to suppress any user-agent
              //     border on the textarea
              //   - `outlineStyle: 'none'` to drop the web focus ring
              //   - `resize: 'none'` so the textarea has no drag
              //     handle (the auto-grow hook owns the height)
              // The last two are web-only DOM-input style keys —
              // react-native-macos rejects them ("Invalid props.style key
              // `outlineStyle`"), so gate them to web.
              { paddingTop: 9, paddingBottom: 9, borderWidth: 0 },
              ...(Platform.OS === 'web'
                ? [{ outlineStyle: 'none', resize: 'none' } as unknown as TextStyle]
                : []),
            ]}
            value={text}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onSelectionChange={handleSelectionChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            // Placeholder per Figma `2804:26708`: `#6B7280` on dark.
            placeholderTextColor={
              colorScheme === 'dark' ? '#6B7280' : colors.text.tertiary
            }
            multiline={isMultiline}
            scrollEnabled={state === 'longText'}
            textAlignVertical="center"
            // Imperative selection override — set by the host
            // immediately after a toolbar / shortcut action so the
            // caret lands at the post-action position instead of
            // where the OS would have left it. Cleared by the host
            // on the next `onSelectionRangeChange`.
            {...(selection !== undefined ? { selection } : {})}
          />
        </View>
      </View>
    );
  },
);

RichTextEditorInner.displayName = 'RichTextEditor';
export const RichTextEditor = React.memo(RichTextEditorInner);
